import { ReactElement, isValidElement, useId } from "react";
import cx from "classnames";
import textFieldStyles from "../TextField/TextField.module.scss";
import dateFieldYearlessRangeStyles from "./DateFieldYearlessRange.module.scss";
import { Helper, HelperProps } from "../../internal/components";
import { warnDeprecatedErrorUsage } from "../FieldMessage";
import { LayoutUtilProps, Size } from "../../types";
import {
  useLayoutPropsUtil,
  useOptionallyControlledState,
} from "../../internal/hooks";
import FieldLabel, { FieldLabelProps } from "../FieldLabel";
import { YearlessDate, YearlessDateMode } from "../DateFieldYearless/types";
import Icon from "../Icon";
import ArrowForward from "@servicetitan/hammer-icon/mdi/filled/arrow_forward.svg";
import { YearlessDateInputWithPicker } from "../DateFieldYearless/internal/YearlessDateInputWithPicker";

export type DateFieldYearlessRangeValue = {
  startDate: YearlessDate | null;
  endDate: YearlessDate | null;
};

export type DateFieldYearlessRangeChange = {
  startDate: YearlessDate | null;
  endDate: YearlessDate | null;
};

export type DateFieldYearlessRangeChangeHandler = (
  change: DateFieldYearlessRangeChange,
) => void;

export type DateFieldYearlessRangeProps = {
  /**
   * The controlled value of the date field
   */
  value?: DateFieldYearlessRangeValue;
  /**
   * The default value for uncontrolled usage
   */
  defaultValue?: DateFieldYearlessRangeValue;
  /**
   * The function to call when the date value changes
   */
  onChange?: DateFieldYearlessRangeChangeHandler;
  /**
   * The date format mode (mm/dd or dd/mm)
   */
  mode?: YearlessDateMode;
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Whether the picker is disabled
   */
  disablePicker?: boolean;
  /**
   * Whether to disable the hint
   */
  disableHint?: boolean;
  /**
   * Error state for the field. Pass `true` to indicate error styling without a message.
   * Pass a string, string[], or ReactElement (deprecated) for error messages.
   */
  error?: boolean | string | ReactElement | string[];
  /**
   * Label for the field group. Always required for accessibility.
   */
  label: string;
  /**
   * When `true`, hides the visible legend while keeping it accessible to screen readers.
   * @default false
   */
  hideLabel?: boolean;
  size?: Extract<Size, "small" | "medium" | "large">;
  description?: HelperProps["description"];
  /**
   * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
   */
  errorAriaLive?: HelperProps["errorAriaLive"];
  /**
   * Warning message(s) to display. Supports a single string or an array of strings.
   */
  warning?: string | string[];
  disabled?: boolean;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
  id?: string;
  startLabel?: string;
  endLabel?: string;
  legendProps?: Omit<FieldLabelProps, "el">;
  moreInfo?: FieldLabelProps["moreInfo"];
} & LayoutUtilProps;

export const DateFieldYearlessRange = (props: DateFieldYearlessRangeProps) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    label,
    moreInfo,
    size,
    hideLabel = false,
    required,
    id: idProp,
    legendProps,
    errorAriaLive: _errorAriaLive,
    onFocus: onFocusProp,
    onBlur: onBlurProp,
    error,
    warning,
    disabled,
    disableHint,
    description,
    mode = "mm/dd",
    startLabel = "Start date",
    endLabel = "End date",
    disablePicker,
  } = componentProps;

  const [value, setValue] =
    useOptionallyControlledState<DateFieldYearlessRangeValue>({
      controlledValue: props.value,
      defaultValue: props.defaultValue,
      onChange: props.onChange,
    });

  const labelUid = useId();
  const id = idProp ?? labelUid;
  const helperUid = useId();
  const moreInfoUid = useId();

  warnDeprecatedErrorUsage("DateFieldYearlessRange", error);

  const errorMessages =
    typeof error === "boolean" || error === undefined ? undefined : error;

  const handleStartDateChange = (startDate: YearlessDate | null) => {
    setValue({
      ...value,
      startDate,
    });
  };

  const handleEndDateChange = (endDate: YearlessDate | null) => {
    setValue({
      ...value,
      endDate,
    });
  };

  const ariaDescribedBy = [helperUid, moreInfo && moreInfoUid]
    .filter(Boolean)
    .join(" ");

  return (
    <fieldset
      className={cx(
        textFieldStyles["textfield"],
        dateFieldYearlessRangeStyles["range-root"],
      )}
      data-anv="date-field-yearless-range"
      style={layoutStyles}
    >
      <FieldLabel
        el="legend"
        moreInfo={moreInfo}
        moreInfoId={moreInfo ? moreInfoUid : undefined}
        required={required}
        {...legendProps}
        className={cx(
          legendProps?.className,
          hideLabel ? dateFieldYearlessRangeStyles["sr-only"] : undefined,
        )}
      >
        {label}
      </FieldLabel>
      <div className={dateFieldYearlessRangeStyles["range-row"]}>
        <FieldLabel
          htmlFor={id + "-" + "start"}
          className={dateFieldYearlessRangeStyles["sr-only"]}
        >
          {startLabel}
        </FieldLabel>
        <YearlessDateInputWithPicker
          id={id + "-" + "start"}
          size={size}
          error={!!error}
          required={required}
          disabled={disabled}
          mode={mode}
          value={value?.startDate}
          onChange={(change) => handleStartDateChange(change.value)}
          aria-describedby={ariaDescribedBy}
          disablePicker={disablePicker}
          onFocus={onFocusProp}
          onBlur={onBlurProp}
        />
        <Icon
          svg={ArrowForward}
          className={dateFieldYearlessRangeStyles["range-arrow"]}
        />
        <FieldLabel
          htmlFor={id + "-" + "end"}
          className={dateFieldYearlessRangeStyles["sr-only"]}
        >
          {endLabel}
        </FieldLabel>
        <YearlessDateInputWithPicker
          id={id + "-" + "end"}
          size={size}
          error={!!error}
          required={required}
          disabled={disabled}
          mode={mode}
          value={value?.endDate}
          onChange={(change) => handleEndDateChange(change.value)}
          aria-labelledby={labelUid}
          aria-describedby={ariaDescribedBy}
          disablePicker={disablePicker}
          onFocus={onFocusProp}
          onBlur={onBlurProp}
        />
      </div>
      {!disableHint || errorMessages || warning || description ? (
        <Helper
          id={helperUid}
          hint={disableHint ? undefined : `Format: ${mode}`}
          showCounter={false}
          error={
            typeof errorMessages === "string" || Array.isArray(errorMessages)
              ? errorMessages
              : undefined
          }
          warning={warning}
          errorMessage={
            isValidElement(errorMessages) ? errorMessages : undefined
          }
          description={description}
        />
      ) : null}
    </fieldset>
  );
};
